import { createClient } from '@propeller-commerce/propeller-sdk-v2'
import Cookies from 'js-cookie'

/**
 * Cookie names for a Propeller access/refresh token.
 *
 * Dormant plumbing for a future registered-user login: guest carts work on the
 * server-side apikey alone (verified against the v2 test env — `startCart`/
 * `cartAddItem` succeed with no token), so there is no anonymous session. When
 * account login is implemented it stores its token here and the proxy client
 * picks it up via `getStoredAccessToken` below, sending `Authorization: Bearer`.
 */
export const ACCESS_TOKEN_COOKIE = 'propeller-access-token'
export const REFRESH_TOKEN_COOKIE = 'propeller-refresh-token'
export const TOKEN_EXPIRY_COOKIE = 'propeller-token-expiry'

/**
 * Read the stored access token. Browser-only: it feeds the SDK's `getAccessToken`
 * resolver in proxy mode. Returns undefined on the server (SSR uses the direct
 * apikey client) and whenever no user is logged in — in which case the proxy
 * falls back to the apikey, which is all a guest cart needs.
 */
export function getStoredAccessToken (): string | undefined {
  if (typeof window === 'undefined') { return undefined }
  return Cookies.get(ACCESS_TOKEN_COOKIE)
}

/**
 * Shared Propeller SDK v2 client.
 *
 * The app uses the SDK's typed service factories
 * (`productService`/`cartService`/…) against the v2 endpoint
 * (`PROPELLER_ENDPOINT` → `…/v2/graphql`). The SDK's generated operations
 * validate against this schema (verified 2026-06-08).
 *
 * Server: calls Propeller directly — API key available in env.
 * Client: routes through the `/api/propeller-graphql` serverMiddleware proxy
 * so the API key stays out of the browser bundle.
 *
 * Holds only static config — safe to reuse across requests. Server and client
 * bundles each get their own module scope, so the singleton is never shared
 * between SSR requests.
 */
let _client: ReturnType<typeof createClient> | null = null

export function getPropellerClient (): ReturnType<typeof createClient> {
  if (_client) { return _client }

  const isServer = typeof window === 'undefined'

  _client = createClient(
    isServer
      ? {
          endpoint: process.env.PROPELLER_ENDPOINT as string,
          apiKey: process.env.PROPELLER_APIKEY,
          securityMode: 'direct'
        }
      : {
          endpoint: '/api/propeller-graphql',
          securityMode: 'proxy',
          // If a user is logged in, attach their bearer token to every proxied
          // request; the proxy (serverMiddleware/propellerGraphql.ts) forwards it
          // upstream. For a guest this returns undefined and the proxy falls back
          // to the apikey — which is all a guest cart needs. The server stays
          // apikey-only on purpose: this singleton has no per-request context, so
          // reading a per-user token here would leak it across SSR requests.
          getAccessToken: getStoredAccessToken
        }
  )

  return _client
}

/**
 * Static Propeller configuration previously fed to the Vue Storefront
 * middleware via `middleware.config.js`. Kept in one place so the hand-rolled
 * queries use the exact same inputs as the v1 integration did.
 */
export const propellerConfig = {
  language: 'NL',
  siteId: 1,
  /** Examenbundel catalog root category. Product/facet queries MUST be scoped to
   * this id — the unscoped default returns the Thieme/demo root (categoryId 1733,
   * sample product `EX-AMPLE-001`/productId 1), which is browsable but not
   * orderable (`cartAddItem` → "Product with ID 1 not found"). Confirmed with
   * Propeller 2026-06-10: Examenbundel root = 1736. */
  catalogRootCategoryId: 1736,
  /** Cookie holding the active cart id — name kept from the VSF era so live
   * users' carts survive the migration deploy. */
  cartCookieName: 'propeller-vsf-cart',
  /** Attribute names shown on product cards/listings (Examenbundel `EXAMEN_*`
   * taxonomy — see constants/products.module.js). */
  productListAttributes: ['EXAMEN_MERK', 'EXAMEN_ONDERWIJSTYPE']
} as const

/**
 * A v2 cart id is a plain UUID. The v1/VSF era stored a differently shaped id in
 * the *same* cookie (e.g. `PROPEL-6AA2DA27-F475-41B4-BD36DD64DE2C75C1`), which
 * CartV2 rejects outright with `Validation failed (uuid is expected)`
 * (`BAD_REQUEST`). Returning visitors carried that cookie across the v2 deploy,
 * so every cart call failed — and because `addItem` only starts a new cart when
 * the cookie is *absent*, and the cookie is only cleared on a completed payment,
 * they stayed stuck (max-age is a year).
 */
const CART_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isValidCartId = (value: unknown): value is string =>
  typeof value === 'string' && CART_ID_PATTERN.test(value)

interface CartCookies {
  get: (name: string) => unknown
  remove: (name: string, options?: Record<string, unknown>) => void
}

/**
 * Read the active cart id, ignoring anything that CartV2 cannot accept. A
 * legacy/corrupt value is dropped so it can't keep breaking every cart call;
 * callers then see "no cart" and start a fresh one.
 */
export const readCartId = (cookies: CartCookies): string | undefined => {
  const value = cookies.get(propellerConfig.cartCookieName)
  if (isValidCartId(value)) {
    return value
  }
  if (value != null) {
    cookies.remove(propellerConfig.cartCookieName, { path: '/' })
  }
  return undefined
}

/**
 * Codes CartV2 returns for a cart id it cannot use: the cart was deleted or has
 * expired (`CART_NOT_FOUND_ERROR`), or the id is not a UUID at all
 * (`BAD_REQUEST` — a v1 cart id still sitting in the cookie). Both are
 * recoverable: forget the cookie and start a fresh cart.
 */
const CART_UNUSABLE_CODES = ['CART_NOT_FOUND_ERROR', 'BAD_REQUEST']

interface GraphQLErrorLike {
  extensions?: { code?: unknown }
}

/** True when a failed cart call means "that cart id is unusable", not "the
 * operation was wrong". Reads the SDK's `GraphQLOperationError.errors`. */
export const isUnusableCartError = (err: unknown): boolean => {
  const errors = (err as { errors?: GraphQLErrorLike[] } | null)?.errors
  return Array.isArray(errors) && errors.some((e) => {
    const code = e?.extensions?.code
    return typeof code === 'string' && CART_UNUSABLE_CODES.includes(code)
  })
}
